import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { PageHeader, Card, Btn, Modal, Field, Input, Select, EmptyState } from "../components/kr";
import HT20ScannerModal from "../components/HT20ScannerModal";
import { toast } from "sonner";
import { Plus, Barcode, X, Trash, PencilSimple, FilePdf, Info } from "@phosphor-icons/react";
import { useRequestDelete } from "./Requests";
import { Pagination, usePagination } from "../components/Pagination";
import useHIDScanner from "../lib/useHIDScanner";
import { PRODUCT_UNITS } from "../lib/productUnits";


export default function Batches() {
  const navigate = useNavigate();
  // --- State Variables ---
  const [batches, setBatches] = useState([]);       // All production batches loaded from the server
  const [materials, setMaterials] = useState([]);   // Raw material master list for selection
  const [schedules, setSchedules] = useState([]);   // Production schedules to link batches to
  const [skus, setSkus] = useState([]);             // SKU master list for output distribution
  const [open, setOpen] = useState(false);          // State to control New Batch creation modal
  const [editOutputs, setEditOutputs] = useState(null); // Holds the batch currently targeted for SKU output edits
  const [details, setDetails] = useState(null);     // Batch shown in the one-click details popup
  const [sort, setSort] = useState("date_desc");    // Sorting configuration for list view
  const [search, setSearch] = useState("");         // Live text search query for batch list filtering
  const [maxWastagePct, setMaxWastagePct] = useState(5);   // Configurable yield threshold from Settings
  const requestDelete = useRequestDelete();         // Custom hook handling batch deletion confirmations

  // --- Data Loading ---
  // Fetches required lookup tables and batch histories simultaneously
  const load = useCallback(async () => {
    const [a, b, c, d, e] = await Promise.all([
      api.get("/batches"),
      api.get("/raw-materials"),
      api.get("/schedules"),
      api.get("/products").catch(() => ({ data: [] })), // Graceful fallback if product catalog is unavailable
      api.get("/settings").catch(() => ({ data: {} })),
    ]);
    setBatches(a.data); setMaterials(b.data); setSchedules(c.data); setSkus(d.data || []);
    const mw = parseFloat((e.data || {}).max_wastage_pct);
    if (!Number.isNaN(mw)) setMaxWastagePct(mw);
  }, []);
  
  useEffect(() => { load(); }, [load]);

  // Deep-link: if the URL carries ?editBatch=<id>, open that batch's Edit
  // Outputs modal as soon as the list is loaded (used by the schedule
  // tooltip on the Production page). Consumes the param so hitting refresh
  // doesn't keep reopening the modal.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const bid = searchParams.get("editBatch");
    if (!bid || !batches.length) return;
    const b = batches.find(x => x.id === bid);
    if (b) {
      setEditOutputs(b);
      const next = new URLSearchParams(searchParams);
      next.delete("editBatch");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batches]);

  // --- Filtering & Sorting Logic ---
  // Memoizes the list calculations to avoid performance bottlenecks on search/sort adjustments
  const view = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = !q ? batches : batches.filter(b =>
      (b.batch_no || "").toLowerCase().includes(q) ||
      (b.product || "").toLowerCase().includes(q)
    );
    const cmp = {
      date_desc: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      date_asc: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      cost_desc: (a, b) => (b.total_material_cost || 0) - (a.total_material_cost || 0),
      cost_asc: (a, b) => (a.total_material_cost || 0) - (b.total_material_cost || 0),
      batch_asc: (a, b) => (a.batch_no || "").localeCompare(b.batch_no || ""),
      product_asc: (a, b) => (a.product || "").localeCompare(b.product || ""),
    }[sort] || ((a, b) => 0);
    return [...filtered].sort(cmp);
  }, [batches, search, sort]);

  // --- Pagination Controls ---
  const { page, setPage, pageSize, setPageSize } = usePagination(20);
  useEffect(() => { setPage(1); }, [search, sort, setPage]); // Reset to page 1 whenever filters change
  const paged = view.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        label="PRODUCTION OUTPUT"
        title="Daily Batches"
        subtitle="Each batch consumes multiple raw materials (FIFO) and produces multiple SKU outputs. COGS is auto-split."
        action={<Btn onClick={() => setOpen(true)} data-testid="add-batch"><Plus size={14} /> New Batch</Btn>}
      />

      <Card className="!p-0">
        {/* Search and Sort Toolbar */}
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-zinc-200">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by batch # or product…"
            data-testid="batches-search"
            className="flex-1 min-w-[200px] px-3 py-2 border border-zinc-300 text-sm font-mono"
          />
          <Select value={sort} onChange={e => setSort(e.target.value)} data-testid="batches-sort" className="!w-auto">
            <option value="date_desc">Date: newest first</option>
            <option value="date_asc">Date: oldest first</option>
            <option value="cost_desc">Cost: high → low</option>
            <option value="cost_asc">Cost: low → high</option>
            <option value="batch_asc">Batch # A → Z</option>
            <option value="product_asc">Product A → Z</option>
          </Select>
        </div>

        {/* Yield Trend Chart — last 30 batches wastage % */}
        <YieldTrendChart batches={batches} threshold={maxWastagePct} />

        {/* Batch Table List Rendering */}
        {view.length === 0 ? (
          <div className="p-6">
            <EmptyState 
              title={batches.length === 0 ? "No batches yet" : "No batches match"} 
              hint={batches.length === 0 ? "Create your first batch — it will auto-deduct raw material from inventory (FIFO)." : "Try clearing the search."} 
            />
          </div>
        ) : (
          <div className="divide-y divide-zinc-200">
            {paged.map(b => {
              // Gracefully handle legacy single-material batches
              const rmList = b.raw_materials || [{
                raw_material_name: b.raw_material_name, weight_used: b.weight_used, unit: b.unit, cost: 0,
              }];
              const outputs = b.outputs || [];
              // Red-flag batches whose wastage exceeds the settings threshold.
              const totalInKg = Number(b.total_weight_used || 0);
              const wastePctVal = totalInKg > 0 ? (Number(b.wastage_kg || 0) / totalInKg * 100) : 0;
              const overWaste = totalInKg > 0 && wastePctVal > maxWastagePct;
              return (
                <div
                  key={b.id}
                  className={`p-5 ${overWaste ? "bg-red-50/60 border-l-4 border-red-500" : ""}`}
                  data-testid={`batch-${b.batch_no}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-mono text-orange-700 font-bold text-lg">{b.batch_no}</div>
                      <div className="font-display text-xl font-bold mt-0.5">{b.product}</div>
                      {b.description && (
                        <div
                          className="text-sm text-zinc-700 mt-0.5 italic max-w-md truncate"
                          title={b.description}
                          data-testid={`batch-description-${b.batch_no}`}
                        >
                          {b.description}
                        </div>
                      )}
                      <div className="text-xs font-mono text-zinc-500 mt-1">
                        {new Date(b.created_at).toLocaleString("en-IN")} · by {b.created_by}
                      </div>
                      {b.schedule_id && (() => {
                        const sched = schedules.find(s => s.id === b.schedule_id);
                        if (!sched) return null;
                        return (
                          <button
                            type="button"
                            onClick={() => navigate(`/production?highlight=${sched.id}`)}
                            className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest bg-indigo-50 text-indigo-800 border border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300 px-2 py-0.5 rounded transition-colors"
                            data-testid={`batch-schedule-backlink-${b.batch_no}`}
                            title={`Go to schedule · ${sched.scheduled_date} · target ${sched.target_qty}${sched.unit}`}
                          >
                            <span className="font-bold">↗ Schedule</span>
                            <span className="text-indigo-600">{sched.scheduled_date} · {sched.target_qty}{sched.unit}</span>
                          </button>
                        );
                      })()}
                    </div>
                    <div className="text-right">
                      <div className="kr-label">Total material cost</div>
                      <div className="font-mono text-lg font-bold">₹{(b.total_material_cost || 0).toLocaleString("en-IN")}</div>
                      {typeof b.total_weight_used === "number" && (
                        <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{b.total_weight_used} {b.unit || "kg"} consumed</div>
                      )}
                      
                      {/* Batch Action Context Menu */}
                      <div className="mt-2 flex flex-col items-end gap-1">
                        {/* Download Printable Cost Sheet PDF */}
                        <button
                          data-testid={`cost-sheet-${b.batch_no}`}
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API}/batches/${b.id}/cost-sheet.pdf`, { credentials: "include" });
                              if (!res.ok) throw new Error("Cost sheet failed");
                              const blob = await res.blob();
                              const a = document.createElement("a");
                              a.href = URL.createObjectURL(blob);
                              a.download = `cost-sheet-${b.batch_no}.pdf`;
                              a.click();
                              setTimeout(() => URL.revokeObjectURL(a.href), 1000);
                            } catch (e) {
                              toast.error(e.message || "Failed to download cost sheet");
                            }
                          }}
                          className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 font-bold uppercase tracking-wider"
                          title="Download A4 cost sheet PDF"
                        >
                          <FilePdf size={12} weight="bold" /> Cost sheet
                        </button>
                        
                        {/* One-click details popup — full COGS breakdown */}
                        <button
                          data-testid={`details-${b.batch_no}`}
                          onClick={() => setDetails(b)}
                          className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:text-indigo-900 font-bold uppercase tracking-wider"
                          title="Full batch breakdown"
                        >
                          <Info size={12} weight="bold" /> Details
                        </button>

                        {/* Manage SKU Outputs & COGS Split */}
                        <button
                          data-testid={`edit-outputs-${b.batch_no}`}
                          onClick={() => setEditOutputs(b)}
                          className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 font-bold uppercase tracking-wider"
                          title="Add or edit output SKUs (recomputes COGS)"
                        >
                          <PencilSimple size={12} weight="bold" /> Edit outputs
                        </button>
                        
                        {/* Delete Entire Batch and Restore Inventory Stocks */}
                        <button
                          data-testid={`del-batch-${b.batch_no}`}
                          onClick={() => requestDelete({
                            kind: "delete_batch",
                            id: b.id,
                            label: `Batch ${b.batch_no} · ${b.product}`,
                            directDeleteUrl: `/batches/${b.id}`,
                            onDone: load,
                          })}
                          className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-bold uppercase tracking-wider"
                        >
                          <Trash size={12} weight="bold" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Raw materials consumed summary */}
                  <div className="mt-3">
                    <div className="kr-label mb-1.5">Raw Materials Consumed</div>
                    <div className="flex flex-wrap gap-2">
                      {rmList.map((r, x) => (
                        <span key={x} className="font-mono text-[11px] bg-orange-50 border border-orange-300 px-2 py-1">
                          <b>{r.raw_material_name}</b> · {r.weight_used}{r.unit || "kg"}
                          {r.cost > 0 && <span className="text-zinc-500"> · ₹{r.cost.toLocaleString("en-IN")}</span>}
                        </span>
                      ))}
                    </div>
                    {/* Specific physical barcodes scanned for FIFO validation */}
                    {b.consumed_items && b.consumed_items.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {b.consumed_items.map((c, x) => (
                          <span key={x} className="font-mono text-[10px] bg-zinc-100 border border-zinc-300 px-1.5 py-0.5 text-zinc-600">
                            {c.barcode || "pool"} · {c.weight_consumed}{c.unit || ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* SKU outputs list and individual dynamic COGS breakdown */}
                  <div className="mt-3">
                    <div className="kr-label mb-1.5">SKU Outputs & COGS</div>
                    {outputs.length === 0 ? (
                      <div className="text-xs text-red-600 italic">
                        No outputs yet — click &quot;Edit outputs&quot; to add SKUs so COGS can be split.
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {outputs.map((o, x) => (
                          <span key={x} className="font-mono text-[11px] bg-green-50 border border-green-300 px-2 py-1">
                            <b>{o.name}</b>{o.sku_code ? ` (${o.sku_code})` : ""} · {o.qty} {o.unit || "pcs"} · <span className="text-green-800">₹{o.cogs_per_unit}/unit · ₹{o.cogs_total} total</span>
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Compact yield chip — Output % / Waste % / Sample % of RM in */}
                    {(() => {
                      const totalIn = Number(b.total_weight_used || 0);
                      if (totalIn <= 0) return null;
                      const outW = outputs.reduce((s, o) => s + (Number(o.weight_kg) || 0), 0);
                      const waste = Number(b.wastage_kg || 0);
                      const sample = Number(b.sampling_kg || 0);
                      const wastePct = totalIn > 0 ? (waste / totalIn * 100) : 0;
                      const pct = (n) => (n / totalIn * 100).toFixed(1);
                      const overThreshold = wastePct > maxWastagePct;
                      return (
                        <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] font-mono items-center" data-testid={`batch-yield-${b.id}`}>
                          <span className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded">Output {pct(outW)}%</span>
                          <span className={`px-1.5 py-0.5 rounded border ${overThreshold ? "bg-red-100 border-red-400 text-red-800 font-bold" : "bg-red-50 border-red-200 text-red-700"}`}
                                title={overThreshold ? `Exceeds max wastage ${maxWastagePct}% set in Settings` : ""}>
                            Waste {pct(waste)}%{overThreshold ? " ⚠" : ""}
                          </span>
                          <span className="bg-blue-50 border border-blue-200 text-blue-700 px-1.5 py-0.5 rounded">Sample {pct(sample)}%</span>
                          {overThreshold && (
                            <span className="text-red-700 font-bold uppercase tracking-wider" data-testid={`batch-wastage-warn-${b.id}`}>
                              Over yield limit
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <Pagination
          page={page}
          pageSize={pageSize}
          total={view.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testId="batch-pager"
        />
      </Card>

      {open && <NewBatchModal materials={materials} schedules={schedules} skus={skus}
        onClose={() => setOpen(false)} onAdded={() => { setOpen(false); load(); }} />}
      {editOutputs && <EditOutputsModal batch={editOutputs} skus={skus} materials={materials}
        onClose={() => setEditOutputs(null)} onSaved={() => { setEditOutputs(null); load(); }} />}
      {details && <BatchDetailsModal batch={details} onClose={() => setDetails(null)} />}
    </div>
  );
}


// ---------------------------------------------------------- Batch details popup
// One-click view of everything about a batch: raw materials, misc costs,
// total production cost, output SKUs and per-SKU COGS.
function BatchDetailsModal({ batch, onClose }) {
  const rms = batch.raw_materials || [];
  const misc = batch.misc_costs || [];
  const outs = batch.outputs || [];
  const consumed = batch.consumed_items || [];
  const rmCost = Number(batch.total_material_cost || 0);
  const miscTotal = Number(batch.total_misc_cost ||
    misc.reduce((s, m) => s + (parseFloat(m.amount) || 0), 0));
  const prodCost = Number(batch.total_production_cost || (rmCost + miscTotal));
  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

  return (
    <Modal open onClose={onClose} title={`Batch ${batch.batch_no} · ${batch.product}`} size="lg">
      <div className="space-y-4" data-testid="batch-details">
        {/* Headline KPI tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="bg-orange-50 border border-orange-200 p-3">
            <div className="kr-label">Raw materials</div>
            <div className="font-mono font-black text-lg">{inr(rmCost)}</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 p-3">
            <div className="kr-label">Misc costs</div>
            <div className="font-mono font-black text-lg">{inr(miscTotal)}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 p-3">
            <div className="kr-label">Total production</div>
            <div className="font-mono font-black text-lg">{inr(prodCost)}</div>
          </div>
          <div className="bg-zinc-50 border border-zinc-200 p-3">
            <div className="kr-label">Weight consumed</div>
            <div className="font-mono font-black text-lg">{batch.total_weight_used || 0}{batch.unit || "kg"}</div>
          </div>
        </div>

        {/* Yield breakdown — output / wastage / sampling as % of RM consumed */}
        {(() => {
          const totalIn = Number(batch.total_weight_used || 0);
          if (totalIn <= 0) return null;
          // Sum finished-goods output weight (qty × pack_size), fall back to
          // output_qty or 0 when packs don't carry weight.
          const outWeight = outs.reduce((s, o) => s + (Number(o.weight_kg) || 0), 0)
            || Number(batch.output_weight_kg || 0)
            || 0;
          const waste = Number(batch.wastage_kg || 0);
          const sample = Number(batch.sampling_kg || 0);
          const pct = (n) => totalIn > 0 ? (n / totalIn * 100).toFixed(1) : "0.0";
          const unacc = Math.max(0, totalIn - outWeight - waste - sample);
          return (
            <div className="border border-zinc-200 bg-white p-3" data-testid="batch-yield-breakdown">
              <div className="flex items-center justify-between mb-2">
                <div className="kr-label">Yield breakdown</div>
                <div className="text-[10px] font-mono text-zinc-500">of {totalIn}{batch.unit || "kg"} raw material</div>
              </div>
              {/* Segmented bar */}
              <div className="flex w-full h-2 rounded overflow-hidden border border-zinc-200">
                <div className="bg-emerald-500" style={{ width: `${(outWeight / totalIn) * 100}%` }} title={`Output ${pct(outWeight)}%`} />
                <div className="bg-red-400" style={{ width: `${(waste / totalIn) * 100}%` }} title={`Wastage ${pct(waste)}%`} />
                <div className="bg-blue-400" style={{ width: `${(sample / totalIn) * 100}%` }} title={`Sampling ${pct(sample)}%`} />
                {unacc > 0 && (
                  <div className="bg-zinc-300" style={{ width: `${(unacc / totalIn) * 100}%` }} title={`Unaccounted ${pct(unacc)}%`} />
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-[10px] font-mono">
                <div><span className="inline-block w-2 h-2 bg-emerald-500 rounded-sm mr-1" />Output {pct(outWeight)}% ({outWeight.toFixed(2)}{batch.unit || "kg"})</div>
                <div><span className="inline-block w-2 h-2 bg-red-400 rounded-sm mr-1" />Wastage {pct(waste)}% ({waste.toFixed(2)}{batch.unit || "kg"})</div>
                <div><span className="inline-block w-2 h-2 bg-blue-400 rounded-sm mr-1" />Sampling {pct(sample)}% ({sample.toFixed(2)}{batch.unit || "kg"})</div>
                {unacc > 0 && (
                  <div className="text-zinc-500"><span className="inline-block w-2 h-2 bg-zinc-300 rounded-sm mr-1" />Unaccounted {pct(unacc)}%</div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Raw material breakdown */}
        <div>
          <div className="kr-label mb-2">Raw materials consumed</div>
          {rms.length === 0 ? (
            <div className="text-xs text-zinc-500 italic">None recorded.</div>
          ) : (
            <table className="kr-table w-full text-xs">
              <thead><tr><th>Material</th><th>Weight</th><th>Cost</th></tr></thead>
              <tbody>
                {rms.map((r, x) => (
                  <tr key={x}>
                    <td className="font-bold">{r.raw_material_name}</td>
                    <td className="font-mono">{r.weight_used}{r.unit || "kg"}</td>
                    <td className="font-mono">{inr(r.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {consumed.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {consumed.map((c, x) => (
                <span key={x} className="font-mono text-[10px] bg-zinc-100 border border-zinc-300 px-1.5 py-0.5 text-zinc-600">
                  {c.barcode || "pool"} · {c.weight_consumed}{c.unit || ""}
                </span>
              ))}
            </div>
          )}
          {(batch.rm_audit || []).length > 0 && (
            <div className="mt-3">
              <div className="kr-label mb-1">RM audit trail</div>
              <div className="border border-zinc-200 rounded divide-y divide-zinc-100" data-testid="details-rm-audit">
                {batch.rm_audit.slice().reverse().map((a, i) => {
                  const when = a.at ? new Date(a.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "";
                  const badge = a.action === "create" ? "bg-blue-50 text-blue-700 border-blue-200"
                    : a.action === "add" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : a.action === "edit" ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-red-50 text-red-700 border-red-200";
                  return (
                    <div key={i} className="flex flex-wrap items-center gap-2 text-[11px] font-mono px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded border ${badge} uppercase tracking-wider font-bold`}>{a.action}</span>
                      <span className="font-bold text-zinc-800">{a.raw_material_name}</span>
                      <span className="text-zinc-600">{a.from_weight}→{a.to_weight}{a.unit || "kg"}</span>
                      {a.cost_delta !== 0 && (
                        <span className={a.cost_delta > 0 ? "text-emerald-700" : "text-red-700"}>
                          {a.cost_delta > 0 ? "+" : ""}{inr(a.cost_delta)}
                        </span>
                      )}
                      <span className="text-zinc-500 ml-auto">{a.by} · {when}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Misc costs */}
        <div>
          <div className="kr-label mb-2">Miscellaneous costs</div>
          {misc.length === 0 ? (
            <div className="text-xs text-zinc-500 italic">None recorded.</div>
          ) : (
            <table className="kr-table w-full text-xs">
              <thead><tr><th>Categories</th><th>Amount</th><th>Notes</th></tr></thead>
              <tbody>
                {misc.map((m, x) => (
                  <tr key={x}>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {(m.categories || []).map(c => (
                          <span key={c} className="bg-purple-100 border border-purple-300 text-purple-800 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5">{c}</span>
                        ))}
                      </div>
                    </td>
                    <td className="font-mono">{inr(m.amount)}</td>
                    <td className="text-zinc-600">{m.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Output SKUs + COGS */}
        <div>
          <div className="kr-label mb-2">Output SKUs · COGS split</div>
          {outs.length === 0 ? (
            <div className="text-xs text-red-600 italic">No output SKUs recorded — click "Edit outputs" to add.</div>
          ) : (
            <table className="kr-table w-full text-xs">
              <thead><tr><th>SKU</th><th>Qty</th><th>Weight</th><th>COGS / unit</th><th>COGS total</th></tr></thead>
              <tbody>
                {outs.map((o, x) => (
                  <tr key={x}>
                    <td className="font-bold">
                      {o.name}
                      {o.sku_code && <span className="text-[10px] font-mono text-zinc-500 ml-2">({o.sku_code})</span>}
                    </td>
                    <td className="font-mono">{o.qty} {o.unit || "packets"}</td>
                    <td className="font-mono text-zinc-500">{o.output_weight_kg ? `${o.output_weight_kg}kg` : "—"}</td>
                    <td className="font-mono font-bold text-emerald-700">{inr(o.cogs_per_unit)}</td>
                    <td className="font-mono">{inr(o.cogs_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {batch.notes && (
          <div className="text-xs text-zinc-600 bg-zinc-50 border-l-2 border-zinc-400 p-2">
            <span className="kr-label">Notes:</span> {batch.notes}
          </div>
        )}

        <div className="flex justify-end">
          <Btn onClick={onClose}>Close</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------- New Batch Modal (multi-RM)
function NewBatchModal({ materials, schedules, skus, onClose, onAdded }) {
  const [f, setF] = useState({
    product: "Potato Chips",
    schedule_id: "",
    notes: "",
  });
  // Local dynamic structures for consuming multiple raw materials and assigning multiple outputs
  const [rms, setRms] = useState([{ raw_material_id: materials[0]?.id || "", weight_used: "", barcodes_used: [] }]);
  const [outs, setOuts] = useState([]);
  const [miscs, setMiscs] = useState([]);   // Ad-hoc costs (oil, packaging, gas, labour, spices...)
  // Yield accounting (informational — kg lost to wastage and taken for QA sampling).
  const [wastageKg, setWastageKg] = useState("");
  const [samplingKg, setSamplingKg] = useState("");
  const [wastageReason, setWastageReason] = useState("");
  const [scanIdx, setScanIdx] = useState(null);   // Tracks which active raw-material index row is currently listening for physical scanner input
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setF({ ...f, [k]: v });

  // --- Dynamic Array Modifier Helpers ---
  const updRm = (i, k, v) => { const c = [...rms]; c[i] = { ...c[i], [k]: v }; setRms(c); };
  const addRm = () => setRms([...rms, { raw_material_id: materials[0]?.id || "", weight_used: "", barcodes_used: [] }]);
  const rmRm = (i) => setRms(rms.filter((_, x) => x !== i));

  // Update the "used from bag" amount for a specific scanned bag.
  // Recomputes the row's total weight_used = sum of per-bag used weights.
  const updBagUse = (rowIdx, code, val) => {
    const c = [...rms];
    const row = c[rowIdx];
    const parsed = parseFloat(val);
    const avail = (row.barcode_available || {})[code] ?? 0;
    // Clamp to [0, avail]. Empty string is allowed while typing.
    let useVal = val;
    if (!Number.isNaN(parsed)) {
      useVal = Math.max(0, Math.min(avail, parsed));
    }
    const nextUse = { ...(row.barcode_use || {}), [code]: useVal };
    const totalUsed = (row.barcodes_used || []).reduce((s, b) => s + (parseFloat(nextUse[b]) || 0), 0);
    c[rowIdx] = { ...row, barcode_use: nextUse, weight_used: totalUsed ? totalUsed.toFixed(3) : "" };
    setRms(c);
  };

  // Update the operator note for a specific scanned bag (why is stock being left?).
  const updBagNote = (rowIdx, code, val) => {
    const c = [...rms];
    const row = c[rowIdx];
    c[rowIdx] = { ...row, barcode_notes: { ...(row.barcode_notes || {}), [code]: val } };
    setRms(c);
  };

  const updOut = (i, k, v) => { const c = [...outs]; c[i] = { ...c[i], [k]: v }; setOuts(c); };
  const addOut = () => setOuts([...outs, { sku_id: "", sku_code: "", name: "", qty: "", unit: "packets" }]);
  const rmOut = (i) => setOuts(outs.filter((_, x) => x !== i));

  const updMisc = (i, k, v) => { const c = [...miscs]; c[i] = { ...c[i], [k]: v }; setMiscs(c); };
  const toggleMiscCat = (i, cat) => {
    const c = [...miscs];
    const cur = new Set(c[i].categories || []);
    cur.has(cat) ? cur.delete(cat) : cur.add(cat);
    c[i] = { ...c[i], categories: Array.from(cur) };
    setMiscs(c);
  };
  const addMisc = () => setMiscs([...miscs, { categories: [], amount: "", notes: "" }]);
  const rmMisc = (i) => setMiscs(miscs.filter((_, x) => x !== i));

  // Populates SKU details when an existing SKU is picked from drop down
  const pickSku = (i, id) => {
    const sku = skus.find(s => s.id === id);
    if (!sku) { updOut(i, "sku_id", ""); return; }
    const c = [...outs];
    c[i] = { ...c[i], sku_id: id, sku_code: sku.sku_code || "", name: sku.name, unit: sku.unit || "packets" };
    setOuts(c);
  };

  // Sync batch setup values with chosen production schedules
  const onScheduleChange = (id) => {
    const s = schedules.find(x => x.id === id);
    if (s) setF({ ...f, schedule_id: id, product: s.product });
    else setF({ ...f, schedule_id: id });
  };

  // --- Physical Helett HT20 USB HID Scanner Hook ---
  // Listens globally to keyboard event patterns generated by the physical scanner hardware.
  useHIDScanner({
    onScan: (code) => {
      if (scanIdx !== null) {
        addScanned(code);
      } else {
        toast.warning("Please click 'Scan' on a raw material row first so I know where to add this barcode.");
      }
    },
    enabled: true, // Listens continuously as long as this Modal is mounted
  });

  // Validates scanned barcodes, checks stock compatibility, and increments total weight dynamically.
  // Loose mode: if the scanned bag belongs to a DIFFERENT raw material than the current row,
  // we auto-switch the row to that RM (or, if that RM is already on another row, redirect the
  // scan into that row) instead of rejecting the scan outright.
  const addScanned = async (code) => {
    if (scanIdx === null) return;
    try {
      const r = await api.get(`/inventory/by-barcode/${code}`);
      let idx = scanIdx;
      let row = rms[idx];

      if (r.data.raw_material_id !== row.raw_material_id) {
        // Prefer an existing row already targeting that RM so we don't overwrite
        // a partially-filled row the user set up.
        const existingIdx = rms.findIndex(x => x.raw_material_id === r.data.raw_material_id);
        if (existingIdx !== -1 && existingIdx !== idx) {
          idx = existingIdx;
          row = rms[existingIdx];
          setScanIdx(existingIdx);
          toast.info(`Redirected to existing ${r.data.raw_material_name} line`);
        } else if (!row.weight_used && !(row.barcodes_used || []).length) {
          // Empty row — safe to auto-switch its selected RM.
          row = { ...row, raw_material_id: r.data.raw_material_id };
          const c = [...rms]; c[idx] = row; setRms(c);
          toast.info(`Switched line to ${r.data.raw_material_name}`);
        } else {
          // Row already has data for a different RM — refuse to overwrite silently.
          toast.error(`This line already has ${row.raw_material_id ? "another material's" : ""} bags. Add a new RM row for ${r.data.raw_material_name}.`);
          return;
        }
      }

      // Ensure no double scans in the same session
      const already = (row.barcodes_used || []).find(b => b === code);
      if (already) { toast.warning("Already scanned"); return; }

      const newList = [...(row.barcodes_used || []), code];
      const avail = r.data.remaining_weight || 0;

      // Default "used" to full available; user can lower it below to leave a leftover in the bag.
      const c = [...rms];
      c[idx] = {
        ...row,
        barcodes_used: newList,
        barcode_available: { ...(row.barcode_available || {}), [code]: avail },
        barcode_use: { ...(row.barcode_use || {}), [code]: avail },
        weight_used: ((parseFloat(row.weight_used) || 0) + avail).toFixed(3),
        // Preserve legacy field name for removal fallback / older records.
        barcode_weights: { ...(row.barcode_weights || {}), [code]: avail },
      };
      setRms(c);

      toast.success(`Added ${code} · +${avail}${r.data.unit}`);
    } catch {
      toast.error("Barcode not found");
    }
  };

  // Remove a scanned bag from a specific RM row and roll the row weight back.
  const removeScanned = (rowIdx, code) => {
    const row = rms[rowIdx];
    if (!row) return;
    const subtract = (row.barcode_use || {})[code] ?? (row.barcode_weights || {})[code] ?? 0;
    const newList = (row.barcodes_used || []).filter(b => b !== code);
    const { [code]: _rw, ...restWeights } = row.barcode_weights || {};
    const { [code]: _ra, ...restAvail } = row.barcode_available || {};
    const { [code]: _ru, ...restUse } = row.barcode_use || {};
    const newWeight = Math.max(0, (parseFloat(row.weight_used) || 0) - (parseFloat(subtract) || 0));
    const c = [...rms];
    c[rowIdx] = {
      ...row,
      barcodes_used: newList,
      barcode_weights: restWeights,
      barcode_available: restAvail,
      barcode_use: restUse,
      weight_used: newWeight ? newWeight.toFixed(3) : "",
    };
    setRms(c);
    toast.success(`Removed ${code} · −${subtract}${row.unit || ""}`);
  };

  // --- Submit & Save ---
  const submit = async (e) => {
    e.preventDefault();
    const validRms = rms.filter(r => r.raw_material_id && parseFloat(r.weight_used) > 0);
    if (validRms.length === 0) { toast.error("Add at least one raw material with a weight > 0"); return; }
    setBusy(true);
    try {
      const body = {
        product: f.product,
        schedule_id: f.schedule_id || null,
        notes: f.notes,
        raw_materials: validRms.map(r => ({
          raw_material_id: r.raw_material_id,
          weight_used: parseFloat(r.weight_used),
          barcodes_used: r.barcodes_used?.length ? r.barcodes_used : null,
          // Send exact per-bag consumption so leftovers stay in the correct bag.
          bag_weights: r.barcodes_used?.length
            ? Object.fromEntries((r.barcodes_used || []).map(b => [b, parseFloat((r.barcode_use || {})[b]) || 0]))
            : null,
          // Operator note per bag (only meaningful when leftover > 0).
          bag_notes: r.barcodes_used?.length
            ? Object.fromEntries((r.barcodes_used || []).map(b => [b, ((r.barcode_notes || {})[b] || "").trim()]))
            : null,
        })),
        outputs: outs.filter(o => o.name && parseFloat(o.qty) > 0).map(o => ({
          sku_id: o.sku_id || null,
          sku_code: o.sku_code || "",
          name: o.name,
          qty: parseFloat(o.qty),
          unit: o.unit || "packets",
        })),
        misc_costs: miscs.filter(m => parseFloat(m.amount) > 0).map(m => ({
          categories: m.categories?.length ? m.categories : ["other"],
          amount: parseFloat(m.amount),
          notes: m.notes || "",
        })),
        wastage_kg: parseFloat(wastageKg) || 0,
        sampling_kg: parseFloat(samplingKg) || 0,
        wastage_reason: wastageReason || null,
      };
      const r = await api.post("/batches", body);
      toast.success(`Batch ${r.data.batch_no} · ₹${r.data.total_material_cost} COGS across ${r.data.outputs.length} SKU(s)`);
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create batch");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Modal open onClose={onClose} title="Create Daily Batch" size="lg">
        <form onSubmit={submit} className="space-y-4" data-testid="batch-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Linked Schedule (optional)" full>
              <Select value={f.schedule_id} onChange={e => onScheduleChange(e.target.value)} data-testid="batch-schedule">
                <option value="">— None —</option>
                {schedules.map(s => <option key={s.id} value={s.id}>{s.scheduled_date} · {s.product} · {s.target_qty}{s.unit}</option>)}
              </Select>
            </Field>
            <Field label="Batch Product / Name" full>
              <Input value={f.product} onChange={e => upd("product", e.target.value)} required data-testid="batch-product" />
            </Field>
          </div>

          {/* Multi-Raw Material Input Section */}
          <div className="border border-orange-300 bg-orange-50/40 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="kr-label">Raw Materials (FIFO consumption)</div>
              <Btn type="button" variant="secondary" onClick={addRm} data-testid="add-batch-rm">
                <Plus size={12} /> Raw
              </Btn>
            </div>
            <div className="space-y-2">
              {rms.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`batch-rm-row-${i}`}>
                  <div className="col-span-5">
                    <Select value={r.raw_material_id} onChange={e => updRm(i, "raw_material_id", e.target.value)} data-testid={`batch-rm-${i}`}>
                      {materials.map(m => <option key={m.id} value={m.id}>{m.name} ({m.code})</option>)}
                    </Select>
                    {/* Chip list of scanned bags — each shows available and lets you edit the used amount */}
                    {(r.barcodes_used || []).length > 0 && (
                      <div className="mt-1.5 flex flex-col gap-1" data-testid={`batch-rm-chips-${i}`}>
                        {(r.barcodes_used || []).map(code => {
                          const avail = (r.barcode_available || {})[code] ?? (r.barcode_weights || {})[code] ?? 0;
                          const use = (r.barcode_use || {})[code];
                          const useNum = parseFloat(use) || 0;
                          const leftover = Math.max(0, avail - useNum);
                          return (
                            <div
                              key={code}
                              className="flex flex-wrap items-center gap-1.5 bg-orange-100 border border-orange-300 text-orange-900 rounded px-2 py-1 text-[10px] font-mono"
                              data-testid={`batch-rm-chip-${i}-${code}`}
                            >
                              <span className="font-bold">{code}</span>
                              <span className="text-orange-700">use</span>
                              <input
                                type="number"
                                step="0.001"
                                min="0"
                                max={avail}
                                value={use ?? ""}
                                onChange={e => updBagUse(i, code, e.target.value)}
                                className="w-16 border border-orange-300 rounded px-1 py-0.5 text-right"
                                data-testid={`batch-rm-chip-use-${i}-${code}`}
                              />
                              <span className="text-orange-700">/ {avail}{r.unit || ""}</span>
                              {leftover > 0.001 && (
                                <>
                                  <span className="ml-1 bg-green-100 border border-green-300 text-green-800 rounded px-1.5 py-0.5" data-testid={`batch-rm-chip-leftover-${i}-${code}`}>
                                    leftover {leftover.toFixed(3)}{r.unit || ""} → stays in bag
                                  </span>
                                  <input
                                    type="text"
                                    placeholder="why left over? (e.g. returned to store)"
                                    value={(r.barcode_notes || {})[code] || ""}
                                    onChange={e => updBagNote(i, code, e.target.value)}
                                    className="flex-1 min-w-[160px] border border-orange-300 rounded px-1 py-0.5"
                                    data-testid={`batch-rm-chip-note-${i}-${code}`}
                                  />
                                </>
                              )}
                              <button
                                type="button"
                                onClick={() => removeScanned(i, code)}
                                className="ml-auto hover:text-red-700"
                                aria-label={`Remove ${code}`}
                                data-testid={`batch-rm-chip-remove-${i}-${code}`}
                              >
                                <X size={10} weight="bold" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      step="0.001"
                      value={r.weight_used}
                      onChange={e => updRm(i, "weight_used", e.target.value)}
                      placeholder="weight"
                      required
                      readOnly={(r.barcodes_used || []).length > 0}
                      title={(r.barcodes_used || []).length > 0 ? "Auto-computed from per-bag usage above" : ""}
                      data-testid={`batch-rm-w-${i}`}
                    />
                  </div>
                  <div className="col-span-3 flex gap-1">
                    <Btn type="button" variant="secondary" onClick={() => setScanIdx(i)} className="!py-1">
                      <Barcode size={12} /> Scan
                    </Btn>
                    {(r.barcodes_used || []).length > 0 && (
                      <span className="text-[10px] font-mono text-zinc-500 self-center">{r.barcodes_used.length} scanned</span>
                    )}
                  </div>
                  <div className="col-span-1">
                    {rms.length > 1 && (
                      <button type="button" onClick={() => rmRm(i)} className="text-red-600"><X size={16} /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Multi-SKU dynamic outputs allocation section */}
          <div className="border border-green-300 bg-green-50/40 p-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="kr-label">SKU Outputs (optional — can add later)</div>
                <div className="text-[10px] text-zinc-500">COGS splits proportionally to the qty of each SKU.</div>
              </div>
              <Btn type="button" variant="secondary" onClick={addOut} data-testid="add-batch-out">
                <Plus size={12} /> SKU
              </Btn>
            </div>
            {outs.length === 0 ? (
              <p className="text-xs text-zinc-500 italic">No outputs yet. You can still create the batch and add outputs later.</p>
            ) : (
              <div className="space-y-2">
                {outs.map((o, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-5">
                      {skus.length > 0 ? (
                        <Select value={o.sku_id || ""} onChange={e => pickSku(i, e.target.value)} data-testid={`batch-out-sku-${i}`}>
                          <option value="">— Pick SKU or type name below —</option>
                          {skus.map(s => <option key={s.id} value={s.id}>{s.name}{s.sku_code ? ` (${s.sku_code})` : ""}</option>)}
                        </Select>
                      ) : (
                        <Input value={o.name} onChange={e => updOut(i, "name", e.target.value)} placeholder="Product name" required />
                      )}
                      {skus.length > 0 && !o.sku_id && (
                        <Input value={o.name} onChange={e => updOut(i, "name", e.target.value)} placeholder="Or type name" className="mt-1" />
                      )}
                    </div>
                    <div className="col-span-3">
                      <Input type="number" step="0.01" value={o.qty} onChange={e => updOut(i, "qty", e.target.value)} placeholder="qty" required data-testid={`batch-out-qty-${i}`} />
                    </div>
                    <div className="col-span-3">
                      <Select
                        value={o.unit || "packets"}
                        onChange={e => updOut(i, "unit", e.target.value)}
                        data-testid={`batch-out-unit-${i}`}
                      >
                        {PRODUCT_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                        {o.unit && !PRODUCT_UNITS.includes(o.unit) && (
                          <option value={o.unit}>{o.unit} (legacy)</option>
                        )}
                      </Select>
                    </div>
                    <div className="col-span-1">
                      <button type="button" onClick={() => rmOut(i)} className="text-red-600"><X size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Yield accounting — kg lost to wastage or taken out for QA/sample.
              Purely informational (not part of COGS). Powers the yield %. */}
          <div className="border border-zinc-300 bg-zinc-50/60 p-3">
            <div className="kr-label mb-2">Yield Accounting (informational)</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-xs">
                <span className="text-zinc-600 font-mono uppercase tracking-widest text-[10px]">Wastage (kg)</span>
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  value={wastageKg}
                  onChange={e => setWastageKg(e.target.value)}
                  placeholder="0"
                  data-testid="batch-wastage-kg"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs">
                <span className="text-zinc-600 font-mono uppercase tracking-widest text-[10px]">Sampling / QA (kg)</span>
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  value={samplingKg}
                  onChange={e => setSamplingKg(e.target.value)}
                  placeholder="0"
                  data-testid="batch-sampling-kg"
                />
              </label>
            </div>
            {/* Reason tag — only asked for when there's wastage to explain */}
            {parseFloat(wastageKg) > 0 && (
              <div className="mt-3">
                <div className="text-zinc-600 font-mono uppercase tracking-widest text-[10px] mb-1">Wastage reason</div>
                <div className="flex flex-wrap gap-1.5">
                  {["spillage", "machine", "test-run", "other"].map(r => (
                    <button
                      type="button"
                      key={r}
                      onClick={() => setWastageReason(r === wastageReason ? "" : r)}
                      className={`px-2.5 py-1 text-xs font-mono rounded transition-colors border ${wastageReason === r ? "bg-red-600 text-white border-red-700" : "bg-white border-zinc-300 hover:bg-red-50"}`}
                      data-testid={`batch-wastage-reason-${r}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <p className="text-[10px] text-zinc-500 mt-2 italic">
              These kg do NOT reduce COGS — they explain the difference between RM input and finished-goods output.
            </p>
          </div>

          {/* Miscellaneous production costs — oil, spices, packaging, gas, labour, etc.
              These add to total batch cost and get distributed across output SKUs by weight. */}
          <div className="border border-purple-300 bg-purple-50/40 p-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="kr-label">Miscellaneous Costs (oil, packaging, labour, gas…)</div>
                <div className="text-[10px] text-zinc-500">Add costs that aren't scanned RM bags. They roll into each output SKU's COGS.</div>
              </div>
              <Btn type="button" variant="secondary" onClick={addMisc} data-testid="add-batch-misc">
                <Plus size={12} /> Misc
              </Btn>
            </div>
            {miscs.length === 0 ? (
              <p className="text-xs text-zinc-500 italic">No miscellaneous costs. Leave empty if this batch's only cost was scanned RM bags.</p>
            ) : (
              <div className="space-y-3">
                {miscs.map((m, i) => (
                  <div key={i} className="bg-white p-2 rounded border border-purple-200" data-testid={`batch-misc-row-${i}`}>
                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-8">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Category (multi-select)</div>
                        <div className="flex flex-wrap gap-1">
                          {[
                            { k: "oil",       lbl: "Oil" },
                            { k: "spices",    lbl: "Spices / masala" },
                            { k: "packaging", lbl: "Packaging" },
                            { k: "labour",    lbl: "Labour" },
                            { k: "gas",       lbl: "Gas / fuel" },
                            { k: "other",     lbl: "Other" },
                          ].map(({ k, lbl }) => {
                            const on = (m.categories || []).includes(k);
                            return (
                              <button
                                key={k}
                                type="button"
                                onClick={() => toggleMiscCat(i, k)}
                                className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-widest rounded border transition-colors ${
                                  on ? "bg-purple-600 text-white border-purple-700"
                                     : "bg-white text-zinc-600 border-zinc-300 hover:border-purple-400"
                                }`}
                                data-testid={`batch-misc-cat-${i}-${k}`}
                              >{lbl}</button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="col-span-3">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Amount (₹)</div>
                        <Input type="number" step="0.01" value={m.amount}
                               onChange={e => updMisc(i, "amount", e.target.value)}
                               placeholder="0.00"
                               data-testid={`batch-misc-amount-${i}`} />
                      </div>
                      <div className="col-span-1 pt-4">
                        <button type="button" onClick={() => rmMisc(i)} className="text-red-600"><X size={16} /></button>
                      </div>
                    </div>
                    <div className="mt-2">
                      <Input value={m.notes || ""} onChange={e => updMisc(i, "notes", e.target.value)}
                             placeholder="Notes (e.g. 'refined oil 2L used in fry pass 1')"
                             className="text-xs" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Field label="Notes"><Input value={f.notes} onChange={e => upd("notes", e.target.value)} /></Field>

          <div className="flex justify-end gap-2">
            <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
            <Btn type="submit" disabled={busy} data-testid="batch-submit">
              {busy ? "Creating…" : "Create & Deduct (FIFO)"}
            </Btn>
          </div>
        </form>
      </Modal>

      {/* 
        HT20 Physical Scanner Modal Overlay
        Provides interactive feedback when hardware is actively scanning barcodes.
        Works strictly via HID keyboard interception—no camera hardware or webRTC streaming.
      */}
      {scanIdx !== null && (
        <HT20ScannerModal
          currentScannedBarcodes={rms[scanIdx]?.barcodes_used || []}
          onClose={() => setScanIdx(null)}
          onDetected={(code) => {
            addScanned(code);
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------- Edit Outputs Modal
function EditOutputsModal({ batch, skus, materials, onClose, onSaved }) {
  // Pre-fill fields with existing configuration if batch outputs are already created
  const seed = (batch.outputs && batch.outputs.length > 0)
    ? batch.outputs.map(o => ({ ...o }))
    : [{ sku_id: "", sku_code: "", name: "", qty: "", unit: "packets" }];
  const [outs, setOuts] = useState(seed);

  // NEW: incremental raw materials + misc costs to append to this batch
  const [addRms, setAddRms] = useState([]);
  const [addMiscs, setAddMiscs] = useState([]);
  const [scanIdx, setScanIdx] = useState(null);
  const [busy, setBusy] = useState(false);

  // Existing RM lines from the saved batch. `editedWeights[rm_id]` holds the
  // in-flight new weight (as string) while the operator types; `removedRms`
  // is a Set of raw_material_ids the operator marked for full removal.
  const round2 = (n) => Math.round((n || 0) * 100) / 100;
  const round3 = (n) => Math.round((n || 0) * 1000) / 1000;
  const existingRmSummary = React.useMemo(() => {
    // Fold duplicate raw_materials[] rows (same RM added twice) into one row
    // so the operator edits ONE weight per raw material.
    const byRm = new Map();
    for (const r of (batch.raw_materials || [])) {
      const cur = byRm.get(r.raw_material_id) || { ...r, weight_used: 0, cost: 0 };
      cur.weight_used = round3((cur.weight_used || 0) + (r.weight_used || 0));
      cur.cost = round2((cur.cost || 0) + (r.cost || 0));
      byRm.set(r.raw_material_id, cur);
    }
    return Array.from(byRm.values());
  }, [batch]);
  const [editedWeights, setEditedWeights] = useState({});
  const [removedRms, setRemovedRms] = useState(new Set());
  const setEditWeight = (rmId, v) => setEditedWeights(prev => ({ ...prev, [rmId]: v }));
  const toggleRemoveRm = (rmId) => setRemovedRms(prev => {
    const next = new Set(prev);
    next.has(rmId) ? next.delete(rmId) : next.add(rmId);
    // If a row is marked for removal, clear any in-flight edit weight for it.
    if (next.has(rmId)) setEditedWeights(w => { const { [rmId]: _, ...rest } = w; return rest; });
    return next;
  });

  // Existing misc-cost rows on the saved batch — edit in-place + toggle remove.
  const existingMisc = React.useMemo(() => (batch.misc_costs || []).filter(m => m.id), [batch]);
  const [editedMisc, setEditedMisc] = useState({});   // {miscId: {amount, notes}}
  const [removedMisc, setRemovedMisc] = useState(new Set());
  const setMiscField = (mid, k, v) => setEditedMisc(prev => ({ ...prev, [mid]: { ...(prev[mid] || {}), [k]: v } }));
  const toggleRemoveMisc = (mid) => setRemovedMisc(prev => {
    const next = new Set(prev);
    next.has(mid) ? next.delete(mid) : next.add(mid);
    if (next.has(mid)) setEditedMisc(prev => { const { [mid]: _, ...rest } = prev; return rest; });
    return next;
  });

  // Yield accounting state
  const [wastageKg, setWastageKg] = useState(batch.wastage_kg ?? "");
  const [samplingKg, setSamplingKg] = useState(batch.sampling_kg ?? "");
  const [wastageReason, setWastageReason] = useState(batch.wastage_reason || "");

  // Editable core batch fields (batch no + product + description)
  const [batchNoDraft, setBatchNoDraft] = useState(batch.batch_no || "");
  const [productDraft, setProductDraft] = useState(batch.product || "");
  const [descriptionDraft, setDescriptionDraft] = useState(batch.description || "");

  const upd = (i, k, v) => { const c = [...outs]; c[i] = { ...c[i], [k]: v }; setOuts(c); };
  const add = () => setOuts([...outs, { sku_id: "", sku_code: "", name: "", qty: "", unit: "packets" }]);
  const rm = (i) => setOuts(outs.filter((_, x) => x !== i));

  // ---- Additional RM helpers ----
  const updRm = (i, k, v) => { const c = [...addRms]; c[i] = { ...c[i], [k]: v }; setAddRms(c); };
  const addRmRow = () => setAddRms([...addRms, {
    raw_material_id: (materials || [])[0]?.id || "", weight_used: "", barcodes_used: [], barcode_weights: {},
    barcode_available: {}, barcode_use: {},
  }]);
  const rmRmRow = (i) => setAddRms(addRms.filter((_, x) => x !== i));

  // Set exact used weight per scanned bag (edit modal). Total weight is auto-derived.
  const updBagUse = (rowIdx, code, val) => {
    const c = [...addRms];
    const row = c[rowIdx];
    const parsed = parseFloat(val);
    const avail = (row.barcode_available || {})[code] ?? 0;
    let useVal = val;
    if (!Number.isNaN(parsed)) useVal = Math.max(0, Math.min(avail, parsed));
    const nextUse = { ...(row.barcode_use || {}), [code]: useVal };
    const total = (row.barcodes_used || []).reduce((s, b) => s + (parseFloat(nextUse[b]) || 0), 0);
    c[rowIdx] = { ...row, barcode_use: nextUse, weight_used: total ? total.toFixed(3) : "" };
    setAddRms(c);
  };
  const updBagNote = (rowIdx, code, val) => {
    const c = [...addRms];
    const row = c[rowIdx];
    c[rowIdx] = { ...row, barcode_notes: { ...(row.barcode_notes || {}), [code]: val } };
    setAddRms(c);
  };

  // ---- Additional misc helpers ----
  const updMisc = (i, k, v) => { const c = [...addMiscs]; c[i] = { ...c[i], [k]: v }; setAddMiscs(c); };
  const toggleMiscCat = (i, cat) => {
    const c = [...addMiscs];
    const cur = new Set(c[i].categories || []);
    cur.has(cat) ? cur.delete(cat) : cur.add(cat);
    c[i] = { ...c[i], categories: Array.from(cur) };
    setAddMiscs(c);
  };
  const addMiscRow = () => setAddMiscs([...addMiscs, { categories: [], amount: "", notes: "" }]);
  const rmMiscRow = (i) => setAddMiscs(addMiscs.filter((_, x) => x !== i));

  // Scanner for additional RM rows (same loose-mode UX as create)
  const addScanned = async (code) => {
    if (scanIdx === null) return;
    try {
      const r = await api.get(`/inventory/by-barcode/${code}`);
      let idx = scanIdx;
      let row = addRms[idx];
      if (r.data.raw_material_id !== row.raw_material_id) {
        const existingIdx = addRms.findIndex(x => x.raw_material_id === r.data.raw_material_id);
        if (existingIdx !== -1 && existingIdx !== idx) {
          idx = existingIdx; row = addRms[existingIdx]; setScanIdx(existingIdx);
          toast.info(`Redirected to existing ${r.data.raw_material_name} line`);
        } else if (!row.weight_used && !(row.barcodes_used || []).length) {
          row = { ...row, raw_material_id: r.data.raw_material_id };
          const c = [...addRms]; c[idx] = row; setAddRms(c);
          toast.info(`Switched line to ${r.data.raw_material_name}`);
        } else {
          toast.error(`Row already has another material. Add a new RM row for ${r.data.raw_material_name}.`);
          return;
        }
      }
      if ((row.barcodes_used || []).includes(code)) { toast.warning("Already scanned"); return; }
      const avail = r.data.remaining_weight || 0;
      const c = [...addRms];
      c[idx] = { ...row,
        barcodes_used: [...(row.barcodes_used || []), code],
        weight_used: ((parseFloat(row.weight_used) || 0) + avail).toFixed(3),
        barcode_weights: { ...(row.barcode_weights || {}), [code]: avail },
        barcode_available: { ...(row.barcode_available || {}), [code]: avail },
        barcode_use: { ...(row.barcode_use || {}), [code]: avail },
      };
      setAddRms(c);
      toast.success(`Added ${code} · +${avail}${r.data.unit}`);
    } catch {
      toast.error("Barcode not found");
    }
  };
  const removeScanned = (rowIdx, code) => {
    const row = addRms[rowIdx];
    const subtract = (row.barcode_use || {})[code] ?? (row.barcode_weights || {})[code] ?? 0;
    const { [code]: _rw, ...restW } = row.barcode_weights || {};
    const { [code]: _ra, ...restA } = row.barcode_available || {};
    const { [code]: _ru, ...restU } = row.barcode_use || {};
    const newW = Math.max(0, (parseFloat(row.weight_used) || 0) - (parseFloat(subtract) || 0));
    const c = [...addRms];
    c[rowIdx] = { ...row,
      barcodes_used: (row.barcodes_used || []).filter(b => b !== code),
      barcode_weights: restW,
      barcode_available: restA,
      barcode_use: restU,
      weight_used: newW ? newW.toFixed(3) : "",
    };
    setAddRms(c);
  };

  useHIDScanner({
    onScan: (code) => {
      if (scanIdx !== null) addScanned(code);
      else toast.warning("Pick 'Scan' on an additional RM row first.");
    },
    enabled: true,
  });

  const pickSku = (i, id) => {
    const sku = skus.find(s => s.id === id);
    if (!sku) { upd(i, "sku_id", ""); return; }
    const c = [...outs];
    c[i] = { ...c[i], sku_id: id, sku_code: sku.sku_code || "", name: sku.name, unit: sku.unit || "packets" };
    setOuts(c);
  };

  // --- Real-Time COGS preview ---
  const existingProdCost = Number(batch.total_production_cost
    ?? ((batch.total_material_cost || 0) + (batch.total_misc_cost || 0)));
  const addRmCostEstimate = 0;  // FIFO cost is server-computed; we can't preview accurately
  const addMiscEstimate = addMiscs.reduce((s, m) => s + (parseFloat(m.amount) || 0), 0);
  const projectedProdCost = existingProdCost + addMiscEstimate + addRmCostEstimate;
  const totalQty = outs.reduce((s, o) => s + (parseFloat(o.qty) || 0), 0);
  const previewPerUnit = totalQty > 0 ? (projectedProdCost / totalQty).toFixed(2) : "0";

  const save = async (e) => {
    e.preventDefault();
    const validOuts = outs.filter(o => o.name && parseFloat(o.qty) > 0);
    if (validOuts.length === 0) { toast.error("Add at least one output"); return; }
    setBusy(true);
    try {
      // Reductions on existing RM lines. Only send rows that (a) aren't marked
      // for removal and (b) have a numeric edited weight that's different from
      // the current summary weight.
      const edits = existingRmSummary
        .filter(r => !removedRms.has(r.raw_material_id))
        .map(r => ({
          raw_material_id: r.raw_material_id,
          old: Number(r.weight_used || 0),
          next: parseFloat(editedWeights[r.raw_material_id]),
        }))
        .filter(e => !Number.isNaN(e.next) && Math.abs(e.next - e.old) > 0.001)
        .map(e => ({ raw_material_id: e.raw_material_id, new_weight_used: e.next }));

      const removals = Array.from(removedRms);

      const r = await api.put(`/batches/${batch.id}/outputs`, {
        outputs: validOuts.map(o => ({
          sku_id: o.sku_id || null, sku_code: o.sku_code || "",
          name: o.name, qty: parseFloat(o.qty), unit: o.unit || "packs",
        })),
        edit_raw_materials: edits.length ? edits : null,
        remove_raw_materials: removals.length ? removals : null,
        additional_raw_materials: addRms
          .filter(r => r.raw_material_id && parseFloat(r.weight_used) > 0)
          .map(r => ({
            raw_material_id: r.raw_material_id,
            weight_used: parseFloat(r.weight_used),
            barcodes_used: r.barcodes_used?.length ? r.barcodes_used : null,
            bag_weights: r.barcodes_used?.length
              ? Object.fromEntries((r.barcodes_used || []).map(b => [b, parseFloat((r.barcode_use || {})[b]) || 0]))
              : null,
            bag_notes: r.barcodes_used?.length
              ? Object.fromEntries((r.barcodes_used || []).map(b => [b, ((r.barcode_notes || {})[b] || "").trim()]))
              : null,
          })),
        additional_misc_costs: addMiscs
          .filter(m => parseFloat(m.amount) > 0)
          .map(m => ({
            categories: m.categories?.length ? m.categories : ["other"],
            amount: parseFloat(m.amount),
            notes: m.notes || "",
          })),
        edit_misc_costs: (() => {
          const rows = Object.entries(editedMisc)
            .filter(([mid]) => !removedMisc.has(mid))
            .map(([mid, patch]) => {
              const out = { id: mid };
              if (patch.amount !== undefined && patch.amount !== "") out.amount = parseFloat(patch.amount) || 0;
              if (patch.notes !== undefined) out.notes = patch.notes;
              return out;
            })
            .filter(p => p.amount !== undefined || p.notes !== undefined);
          return rows.length ? rows : null;
        })(),
        remove_misc_costs: removedMisc.size ? Array.from(removedMisc) : null,
        wastage_kg: wastageKg === "" ? null : parseFloat(wastageKg) || 0,
        sampling_kg: samplingKg === "" ? null : parseFloat(samplingKg) || 0,
        wastage_reason: wastageReason || null,
        batch_no: (batchNoDraft || "").trim() !== (batch.batch_no || "") ? (batchNoDraft || "").trim() : null,
        product: (productDraft || "").trim() !== (batch.product || "") ? (productDraft || "").trim() : null,
        description: (descriptionDraft || "") !== (batch.description || "") ? (descriptionDraft || "") : null,
      });
      toast.success(`Updated · ${r.data.outputs.length} SKU(s) · ₹${r.data.total_production_cost} total cost`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update batch");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Edit batch — ${batch.batch_no}`} size="lg">
      <form onSubmit={save} className="space-y-4" data-testid="edit-outputs-form">
        {/* Live COGS preview */}
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div className="border border-zinc-200 p-2 bg-zinc-50">
            <div className="kr-label">Batch cost (with additions)</div>
            <div className="font-mono font-bold text-lg">₹{projectedProdCost.toLocaleString("en-IN")}</div>
            {(addMiscEstimate > 0) && (
              <div className="text-[10px] text-purple-700">+ ₹{addMiscEstimate} misc (RM cost added after save)</div>
            )}
          </div>
          <div className="border border-zinc-200 p-2 bg-zinc-50">
            <div className="kr-label">Total output qty</div>
            <div className="font-mono font-bold text-lg">{totalQty}</div>
          </div>
          <div className="border border-zinc-200 p-2 bg-zinc-50">
            <div className="kr-label">COGS / unit (approx)</div>
            <div className="font-mono font-bold text-lg text-orange-700">₹{previewPerUnit}</div>
          </div>
        </div>

        {/* Core batch fields — editable batch number, product name, and description */}
        <div className="border border-zinc-300 bg-white p-3" data-testid="edit-core-fields">
          <div className="kr-label mb-2">Batch Details</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Batch No.">
              <Input
                value={batchNoDraft}
                onChange={e => setBatchNoDraft(e.target.value)}
                placeholder="e.g. B-CHIL-20260215-01"
                data-testid="edit-batch-no-input"
              />
            </Field>
            <Field label="Product">
              <Input
                value={productDraft}
                onChange={e => setProductDraft(e.target.value)}
                placeholder="Product name"
                data-testid="edit-batch-product-input"
              />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Description (optional notes shown on the batch card)">
              <textarea
                value={descriptionDraft}
                onChange={e => setDescriptionDraft(e.target.value)}
                rows={2}
                placeholder="Short note — e.g. re-milled leftover, moisture 8%, packed for QA…"
                className="w-full border border-zinc-300 px-2 py-1.5 text-sm rounded focus:outline-none focus:ring-1 focus:ring-orange-500"
                data-testid="edit-batch-description-input"
              />
            </Field>
          </div>
        </div>

        {/* Existing Raw Materials — editable weight + full remove */}
        {existingRmSummary.length > 0 && (
          <div className="border border-zinc-300 bg-zinc-50/50 p-3" data-testid="edit-existing-rms">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="kr-label">Raw Materials On This Batch</div>
                <div className="text-[10px] text-zinc-500">Reduce weight to refund unused stock back to the bag. Remove to fully undo an RM line.</div>
              </div>
            </div>
            <div className="space-y-2">
              {existingRmSummary.map((r) => {
                const marked = removedRms.has(r.raw_material_id);
                const draft = editedWeights[r.raw_material_id];
                const draftNum = parseFloat(draft);
                const currentWeight = Number(r.weight_used || 0);
                const showDelta = !marked && !Number.isNaN(draftNum) && Math.abs(draftNum - currentWeight) > 0.001;
                const delta = round3(currentWeight - draftNum);
                const audit = (batch.rm_audit || []).filter(a => a.raw_material_id === r.raw_material_id);
                return (
                  <div
                    key={r.raw_material_id}
                    className={`rounded p-2 border ${marked ? "bg-red-50 border-red-200 opacity-70" : "bg-white border-zinc-200"}`}
                    data-testid={`edit-existing-rm-${r.raw_material_id}`}
                  >
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-5 flex flex-col">
                        <span className={`text-sm font-semibold ${marked ? "line-through text-red-700" : "text-zinc-900"}`}>{r.raw_material_name}</span>
                        <span className="text-[10px] font-mono text-zinc-500">was {currentWeight}{r.unit || "kg"} · ₹{round2(r.cost || 0)}</span>
                      </div>
                      <div className="col-span-3">
                        <Input
                          type="number"
                          step="0.001"
                          min="0"
                          max={currentWeight}
                          value={marked ? "" : (draft ?? String(currentWeight))}
                          onChange={e => setEditWeight(r.raw_material_id, e.target.value)}
                          disabled={marked}
                          data-testid={`edit-rm-weight-${r.raw_material_id}`}
                        />
                      </div>
                      <div className="col-span-3 text-[10px] font-mono">
                        {marked ? (
                          <span className="text-red-700 font-bold">Will refund all {currentWeight}{r.unit || "kg"}</span>
                        ) : showDelta ? (
                          <span className={delta > 0 ? "text-green-700 font-bold" : "text-orange-700 font-bold"}>
                            {delta > 0 ? `↩ refund ${delta}${r.unit || "kg"}` : `+${-delta} needs Add More RM`}
                          </span>
                        ) : (
                          <span className="text-zinc-400">no change</span>
                        )}
                      </div>
                      <div className="col-span-1 text-right">
                        <button
                          type="button"
                          onClick={() => toggleRemoveRm(r.raw_material_id)}
                          className={`text-xs font-mono uppercase tracking-wider px-2 py-1 rounded transition-colors ${marked ? "bg-zinc-200 text-zinc-700 hover:bg-zinc-300" : "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"}`}
                          data-testid={`edit-rm-remove-${r.raw_material_id}`}
                          title={marked ? "Undo removal" : "Remove this RM (refunds all consumed bags)"}
                        >
                          {marked ? "Undo" : "Remove"}
                        </button>
                      </div>
                    </div>
                    {/* Compact audit timeline — most recent 4 events */}
                    {audit.length > 0 && (
                      <div className="mt-2 border-l-2 border-zinc-200 pl-2 space-y-0.5"
                           data-testid={`edit-rm-audit-${r.raw_material_id}`}>
                        {audit.slice(-4).reverse().map((a, i) => {
                          const when = a.at ? new Date(a.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "";
                          const badge = a.action === "create" ? "bg-blue-50 text-blue-700 border-blue-200"
                            : a.action === "add" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : a.action === "edit" ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-red-50 text-red-700 border-red-200";
                          return (
                            <div key={i} className="flex flex-wrap items-center gap-1.5 text-[10px] font-mono text-zinc-600">
                              <span className={`px-1.5 py-0.5 rounded border ${badge} uppercase tracking-wider font-bold`}>
                                {a.action}
                              </span>
                              <span className="text-zinc-800">
                                {a.from_weight}→{a.to_weight}{a.unit || "kg"}
                              </span>
                              {a.cost_delta !== 0 && (
                                <span className={a.cost_delta > 0 ? "text-emerald-700" : "text-red-700"}>
                                  {a.cost_delta > 0 ? "+" : ""}₹{a.cost_delta}
                                </span>
                              )}
                              <span className="text-zinc-400">·</span>
                              <span className="text-zinc-500">{a.by}</span>
                              <span className="ml-auto text-zinc-400">{when}</span>
                            </div>
                          );
                        })}
                        {audit.length > 4 && (
                          <div className="text-[10px] font-mono text-zinc-400 italic">
                            + {audit.length - 4} earlier event(s)
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Additional Raw Materials */}
        <div className="border border-orange-300 bg-orange-50/40 p-3">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="kr-label">Add More Raw Materials</div>
              <div className="text-[10px] text-zinc-500">Consumes inventory FIFO. Existing RMs stay untouched.</div>
            </div>
            <Btn type="button" variant="secondary" onClick={addRmRow} data-testid="edit-add-rm">
              <Plus size={12} /> RM
            </Btn>
          </div>
          {addRms.length === 0 ? (
            <p className="text-xs text-zinc-500 italic">No additional raw materials to add.</p>
          ) : (
            <div className="space-y-2">
              {addRms.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`edit-rm-row-${i}`}>
                  <div className="col-span-5">
                    <Select value={r.raw_material_id} onChange={e => updRm(i, "raw_material_id", e.target.value)}>
                      {(materials || []).map(m => <option key={m.id} value={m.id}>{m.name} ({m.code})</option>)}
                    </Select>
                    {(r.barcodes_used || []).length > 0 && (
                      <div className="mt-1.5 flex flex-col gap-1">
                        {(r.barcodes_used || []).map(code => {
                          const avail = (r.barcode_available || {})[code] ?? (r.barcode_weights || {})[code] ?? 0;
                          const use = (r.barcode_use || {})[code];
                          const useNum = parseFloat(use) || 0;
                          const leftover = Math.max(0, avail - useNum);
                          return (
                            <div key={code}
                                 className="flex flex-wrap items-center gap-1.5 bg-orange-100 border border-orange-300 text-orange-900 rounded px-2 py-1 text-[10px] font-mono"
                                 data-testid={`edit-rm-chip-${i}-${code}`}>
                              <span className="font-bold">{code}</span>
                              <span className="text-orange-700">use</span>
                              <input
                                type="number"
                                step="0.001"
                                min="0"
                                max={avail}
                                value={use ?? ""}
                                onChange={e => updBagUse(i, code, e.target.value)}
                                className="w-16 border border-orange-300 rounded px-1 py-0.5 text-right"
                                data-testid={`edit-rm-chip-use-${i}-${code}`}
                              />
                              <span className="text-orange-700">/ {avail}</span>
                              {leftover > 0.001 && (
                                <>
                                  <span className="ml-1 bg-green-100 border border-green-300 text-green-800 rounded px-1.5 py-0.5">
                                    leftover {leftover.toFixed(3)} → stays in bag
                                  </span>
                                  <input
                                    type="text"
                                    placeholder="why left over?"
                                    value={(r.barcode_notes || {})[code] || ""}
                                    onChange={e => updBagNote(i, code, e.target.value)}
                                    className="flex-1 min-w-[140px] border border-orange-300 rounded px-1 py-0.5"
                                    data-testid={`edit-rm-chip-note-${i}-${code}`}
                                  />
                                </>
                              )}
                              <button type="button" onClick={() => removeScanned(i, code)} className="ml-auto hover:text-red-700"><X size={10} weight="bold" /></button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="col-span-3">
                    <Input type="number" step="0.001" value={r.weight_used}
                           onChange={e => updRm(i, "weight_used", e.target.value)}
                           placeholder="weight"
                           readOnly={(r.barcodes_used || []).length > 0}
                           title={(r.barcodes_used || []).length > 0 ? "Auto-computed from per-bag usage" : ""} />
                  </div>
                  <div className="col-span-3 flex gap-1">
                    <Btn type="button" variant="secondary" onClick={() => setScanIdx(i)} className="!py-1">
                      <Barcode size={12} /> Scan
                    </Btn>
                  </div>
                  <div className="col-span-1">
                    <button type="button" onClick={() => rmRmRow(i)} className="text-red-600"><X size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Existing misc-cost lines (editable) */}
        {existingMisc.length > 0 && (
          <div className="border border-purple-300 bg-purple-50/30 p-3" data-testid="edit-existing-misc">
            <div className="kr-label mb-2">Extras On This Batch (misc costs)</div>
            <div className="space-y-2">
              {existingMisc.map(m => {
                const marked = removedMisc.has(m.id);
                const patch = editedMisc[m.id] || {};
                const draftAmt = patch.amount ?? m.amount;
                const draftNotes = patch.notes ?? (m.notes || "");
                const original = Number(m.amount || 0);
                const nextAmt = parseFloat(draftAmt);
                const delta = !Number.isNaN(nextAmt) ? +(nextAmt - original).toFixed(2) : 0;
                return (
                  <div
                    key={m.id}
                    className={`grid grid-cols-12 gap-2 items-center rounded p-2 border ${marked ? "bg-red-50 border-red-200 opacity-70" : "bg-white border-purple-200"}`}
                    data-testid={`edit-existing-misc-${m.id}`}
                  >
                    <div className="col-span-4 flex flex-col gap-0.5">
                      <span className={`text-[10px] font-mono uppercase tracking-widest ${marked ? "text-red-700 line-through" : "text-purple-800"}`}>
                        {(m.categories || ["other"]).join(" · ")}
                      </span>
                      <Input
                        type="text"
                        value={draftNotes}
                        onChange={e => setMiscField(m.id, "notes", e.target.value)}
                        disabled={marked}
                        placeholder="notes"
                        className="text-xs"
                        data-testid={`edit-misc-notes-${m.id}`}
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={draftAmt}
                        onChange={e => setMiscField(m.id, "amount", e.target.value)}
                        disabled={marked}
                        data-testid={`edit-misc-amount-${m.id}`}
                      />
                    </div>
                    <div className="col-span-3 text-[10px] font-mono">
                      {marked ? (
                        <span className="text-red-700 font-bold">Will remove ₹{original.toFixed(2)}</span>
                      ) : Math.abs(delta) > 0.001 ? (
                        <span className={delta < 0 ? "text-green-700 font-bold" : "text-orange-700 font-bold"}>
                          {delta < 0 ? "↩" : "↑"} ₹{delta.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-zinc-400">no change</span>
                      )}
                    </div>
                    <div className="col-span-2 text-right">
                      <button
                        type="button"
                        onClick={() => toggleRemoveMisc(m.id)}
                        className={`text-xs font-mono uppercase tracking-wider px-2 py-1 rounded transition-colors ${marked ? "bg-zinc-200 text-zinc-700 hover:bg-zinc-300" : "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"}`}
                        data-testid={`edit-misc-remove-${m.id}`}
                      >
                        {marked ? "Undo" : "Remove"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Yield Accounting — wastage + sampling kg (editable on saved batch) */}
        <div className="border border-zinc-300 bg-zinc-50/60 p-3">
          <div className="kr-label mb-2">Yield Accounting (informational)</div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-zinc-600 font-mono uppercase tracking-widest text-[10px]">Wastage (kg)</span>
              <Input type="number" step="0.001" min="0" value={wastageKg}
                     onChange={e => setWastageKg(e.target.value)}
                     placeholder="0"
                     data-testid="edit-batch-wastage-kg" />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-zinc-600 font-mono uppercase tracking-widest text-[10px]">Sampling / QA (kg)</span>
              <Input type="number" step="0.001" min="0" value={samplingKg}
                     onChange={e => setSamplingKg(e.target.value)}
                     placeholder="0"
                     data-testid="edit-batch-sampling-kg" />
            </label>
          </div>
          {parseFloat(wastageKg) > 0 && (
            <div className="mt-3">
              <div className="text-zinc-600 font-mono uppercase tracking-widest text-[10px] mb-1">Wastage reason</div>
              <div className="flex flex-wrap gap-1.5">
                {["spillage", "machine", "test-run", "other"].map(r => (
                  <button
                    type="button"
                    key={r}
                    onClick={() => setWastageReason(r === wastageReason ? "" : r)}
                    className={`px-2.5 py-1 text-xs font-mono rounded transition-colors border ${wastageReason === r ? "bg-red-600 text-white border-red-700" : "bg-white border-zinc-300 hover:bg-red-50"}`}
                    data-testid={`edit-batch-wastage-reason-${r}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Additional misc costs */}
        <div className="border border-purple-300 bg-purple-50/40 p-3">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="kr-label">Add More Misc Costs</div>
              <div className="text-[10px] text-zinc-500">Roll into total production cost.</div>
            </div>
            <Btn type="button" variant="secondary" onClick={addMiscRow} data-testid="edit-add-misc">
              <Plus size={12} /> Misc
            </Btn>
          </div>
          {addMiscs.length === 0 ? (
            <p className="text-xs text-zinc-500 italic">No additional misc costs.</p>
          ) : (
            <div className="space-y-3">
              {addMiscs.map((m, i) => (
                <div key={i} className="bg-white p-2 rounded border border-purple-200">
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-8">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Category (multi-select)</div>
                      <div className="flex flex-wrap gap-1">
                        {[
                          { k: "oil", lbl: "Oil" },
                          { k: "spices", lbl: "Spices / masala" },
                          { k: "packaging", lbl: "Packaging" },
                          { k: "labour", lbl: "Labour" },
                          { k: "gas", lbl: "Gas / fuel" },
                          { k: "other", lbl: "Other" },
                        ].map(({ k, lbl }) => {
                          const on = (m.categories || []).includes(k);
                          return (
                            <button key={k} type="button" onClick={() => toggleMiscCat(i, k)}
                                    className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-widest rounded border ${
                                      on ? "bg-purple-600 text-white border-purple-700"
                                         : "bg-white text-zinc-600 border-zinc-300 hover:border-purple-400"}`}>
                              {lbl}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="col-span-3">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Amount (₹)</div>
                      <Input type="number" step="0.01" value={m.amount}
                             onChange={e => updMisc(i, "amount", e.target.value)} placeholder="0.00" />
                    </div>
                    <div className="col-span-1 pt-4">
                      <button type="button" onClick={() => rmMiscRow(i)} className="text-red-600"><X size={16} /></button>
                    </div>
                  </div>
                  <Input value={m.notes || ""} onChange={e => updMisc(i, "notes", e.target.value)}
                         placeholder="Notes" className="text-xs mt-2" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Outputs — same as before */}
        <div className="border border-green-300 bg-green-50/40 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="kr-label">SKU Outputs</div>
            <Btn type="button" variant="secondary" onClick={add} data-testid="add-output-row">
              <Plus size={12} /> SKU
            </Btn>
          </div>
          <div className="space-y-2">
            {outs.map((o, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-5">
                  {skus.length > 0 ? (
                    <Select value={o.sku_id || ""} onChange={e => pickSku(i, e.target.value)} data-testid={`out-sku-${i}`}>
                      <option value="">— Pick SKU —</option>
                      {skus.map(s => <option key={s.id} value={s.id}>{s.name}{s.sku_code ? ` (${s.sku_code})` : ""}</option>)}
                    </Select>
                  ) : null}
                  <Input value={o.name} onChange={e => upd(i, "name", e.target.value)} placeholder="Name" required className={skus.length > 0 ? "mt-1" : ""} />
                </div>
                <div className="col-span-3">
                  <Input type="number" step="0.01" value={o.qty} onChange={e => upd(i, "qty", e.target.value)} placeholder="qty" required data-testid={`out-qty-${i}`} />
                </div>
                <div className="col-span-3">
                  <Select
                    value={o.unit || "packets"}
                    onChange={e => upd(i, "unit", e.target.value)}
                    data-testid={`out-unit-${i}`}
                  >
                    {PRODUCT_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                    {o.unit && !PRODUCT_UNITS.includes(o.unit) && (
                      <option value={o.unit}>{o.unit} (legacy)</option>
                    )}
                  </Select>
                </div>
                <div className="col-span-1">
                  {outs.length > 1 && (
                    <button type="button" onClick={() => rm(i)} className="text-red-600"><X size={16} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="save-outputs">
            {busy ? "Saving…" : "Save & Recompute COGS"}
          </Btn>
        </div>
      </form>

      {scanIdx !== null && (
        <HT20ScannerModal
          currentScannedBarcodes={addRms[scanIdx]?.barcodes_used || []}
          onClose={() => setScanIdx(null)}
          onDetected={(code) => { addScanned(code); }}
        />
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------- YieldTrendChart
// Compact SVG bar chart of wastage% across the last 30 batches (oldest→newest).
// Bars taller than the configured threshold are painted red to draw the eye.
// Hidden entirely when there's no batch with wastage data.
function YieldTrendChart({ batches, threshold }) {
  // 7 / 30 / 90 day windows so operators can zoom out to spot long-term trends.
  const [rangeDays, setRangeDays] = React.useState(30);

  const rows = React.useMemo(() => {
    const cutoff = Date.now() - rangeDays * 86400_000;
    return (batches || [])
      .filter(b => Number(b.total_weight_used || 0) > 0)
      .filter(b => new Date(b.created_at || 0).getTime() >= cutoff)
      .slice()
      .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
      .map(b => {
        const tw = Number(b.total_weight_used || 0);
        const w = Number(b.wastage_kg || 0);
        return {
          id: b.id,
          batch_no: b.batch_no,
          created_at: b.created_at,
          wastage_pct: tw > 0 ? +(w / tw * 100).toFixed(2) : 0,
          wastage_reason: b.wastage_reason || null,
        };
      });
  }, [batches, rangeDays]);

  // Reason breakdown among batches with wastage in the current window.
  const reasonBreakdown = React.useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      if (r.wastage_pct <= 0) continue;
      const k = r.wastage_reason || "untagged";
      map.set(k, (map.get(k) || 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3);
  }, [rows]);

  const anyWastage = React.useMemo(
    () => (batches || []).some(b => Number(b.total_weight_used || 0) > 0 && Number(b.wastage_kg || 0) >= 0),
    [batches]
  );
  if (!anyWastage) return null;
  const max = Math.max(threshold, ...(rows.map(r => r.wastage_pct)), 5);
  const avg = rows.length ? rows.reduce((s, r) => s + r.wastage_pct, 0) / rows.length : 0;
  const over = rows.filter(r => r.wastage_pct > threshold).length;

  const W = 100, H = 60;   // viewBox units
  const barW = W / rows.length;
  const scaleY = (v) => H - (v / max) * H;
  const thresholdY = scaleY(threshold);

  return (
    <div className="p-4 border-b border-zinc-200 bg-zinc-50/60" data-testid="yield-trend-chart">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
        <div>
          <div className="kr-label">Yield trend — wastage %</div>
          <div className="text-[10px] font-mono text-zinc-500">
            {rows.length} batch{rows.length === 1 ? "" : "es"} in last {rangeDays}d · threshold {threshold}%
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-white border border-zinc-200 rounded p-0.5" role="tablist" data-testid="yield-range-toggle">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                type="button"
                onClick={() => setRangeDays(d)}
                className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest rounded transition-colors ${rangeDays === d ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
                data-testid={`yield-range-${d}d`}
                aria-selected={rangeDays === d}
                role="tab"
              >
                {d}d
              </button>
            ))}
          </div>
          <div className="flex gap-3 text-[11px] font-mono">
            <span>Avg <b className={avg > threshold ? "text-red-700" : "text-emerald-700"}>{avg.toFixed(1)}%</b></span>
            <span>Over threshold <b className={over > 0 ? "text-red-700" : "text-emerald-700"}>{over}</b></span>
          </div>
        </div>
      </div>
      {reasonBreakdown.length > 0 && (
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5 text-[10px] font-mono" data-testid="wastage-reason-breakdown">
          <span className="text-zinc-500 uppercase tracking-widest">Top reasons</span>
          {reasonBreakdown.map(([r, n]) => (
            <span key={r} className={`px-1.5 py-0.5 rounded border ${r === "untagged" ? "bg-zinc-100 text-zinc-600 border-zinc-300" : "bg-red-50 text-red-700 border-red-200"}`}>
              {r} · {n}
            </span>
          ))}
        </div>
      )}
      <div className="relative w-full h-[110px] bg-white border border-zinc-200 rounded p-1">
        {rows.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center text-[10px] font-mono text-zinc-400 italic">
            No batches in this window — pick a wider range
          </div>
        ) : (
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-full">
          {/* Threshold line */}
          <line x1={0} x2={W} y1={thresholdY} y2={thresholdY} stroke="#ef4444" strokeWidth="0.3" strokeDasharray="1,1" />
          {rows.map((r, i) => {
            const y = scaleY(r.wastage_pct);
            const h = H - y;
            const over = r.wastage_pct > threshold;
            return (
              <g key={r.id}>
                <rect
                  x={i * barW + barW * 0.15}
                  y={y}
                  width={barW * 0.7}
                  height={Math.max(0.5, h)}
                  fill={over ? "#dc2626" : "#10b981"}
                  data-testid={`yield-bar-${r.batch_no}`}
                >
                  <title>{`${r.batch_no || r.id.slice(0, 6)} · ${r.wastage_pct}% · ${(r.created_at || "").slice(0, 10)}`}</title>
                </rect>
              </g>
            );
          })}
        </svg>
        )}
        <div className="absolute right-1 top-1 text-[9px] font-mono text-red-600 bg-white/70 px-1 rounded pointer-events-none">
          {threshold}% limit
        </div>
      </div>
    </div>
  );
}
